import { GridState } from 'ag-grid-community';
import { mapKeys } from 'lodash';
import { useCallback, useMemo } from 'react';

import { AgGridServerTable } from 'core/components';
import { SortDirection, TableLayoutType } from 'core/enums';
import { AgGridServerTableProvider } from 'core/states';
import { AgGridGetRowsParams } from 'core/types';
import { removeNullValuesNestedObject } from 'core/utils';
import {
  AddJobButton,
  ExportEntityType,
  ExportTimeLogButton,
  UpdateJobNameButton,
  UpdateJobsButton,
} from 'jobs/components/JobList';
import { jobService } from 'jobs/services';
import { IJob } from 'jobs/types';
import { useProjectJobsContext } from 'projects/states';
import { IProject } from 'projects/types';

import { columnDef } from './columns';

interface IProps {
  projectId: number;
}

export const JobList = ({ projectId }: IProps) => {
  const { jobsCount, setJobsCount } = useProjectJobsContext();

  const columnDefs = useMemo(() => columnDef(projectId), [projectId]);

  const defaultColDef = useMemo(() => ({ autoHeight: true }), []);

  const initialState: GridState = useMemo(() => {
    return {
      sort: {
        sortModel: [{ colId: 'name', sort: SortDirection.ASC }],
      },
    };
  }, []);

  const mapFilters = useCallback((filters) => {
    return mapKeys(filters, (_, key) => {
      switch (key) {
        case 'customers':
          return 'customer';
        case 'vendors':
          return 'vendor';
        default:
          return key;
      }
    });
  }, []);

  const getRows = useCallback(
    async ({ filters, ...rest }: AgGridGetRowsParams) => {
      const result = await jobService.getJobs(
        removeNullValuesNestedObject({
          project: projectId,
          filters: mapFilters(filters),
          ...rest,
        })
      );

      if (!jobsCount) {
        setJobsCount(result.count);
      }

      return result;
    },
    [jobsCount, mapFilters, projectId, setJobsCount]
  );

  const actionButtons = useMemo(() => {
    return [
      <AddJobButton
        key="addJobButton"
        projectId={projectId}
        onSubmitSuccess={() => setJobsCount(null)}
      />,
    ];
  }, [projectId, setJobsCount]);

  const selectionActionButtons = useMemo(() => {
    return [
      <UpdateJobNameButton key="updateJobNameButton" />,
      <UpdateJobsButton key="updateJobsButton" />,
      <ExportTimeLogButton
        key="exportTimeLogButton"
        entityType={ExportEntityType.BOM}
        isExportLogAllowed={(selectedRows: IJob[] | IProject[]) =>
          (selectedRows as IJob[])?.every((row: IJob) => row.logAccessAllowed)
        }
      />,
    ];
  }, []);

  return (
    <AgGridServerTableProvider
      tableId="projectJobListTable"
      entityType="Job"
      columnDefs={columnDefs}
      defaultColDef={defaultColDef}
      initialState={initialState}
      getRows={getRows}
      layoutType={TableLayoutType.PROJECT_JOB}
      hasLayoutActions
      hasManageFilters
    >
      <AgGridServerTable
        actionButtons={actionButtons}
        selectionActionButtons={selectionActionButtons}
      />
    </AgGridServerTableProvider>
  );
};
